"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import { ExhibitionPricingService } from "../../../Service/ExhibitionRelatedService"
import type { ExhibitionPricing } from "./types"
import { toast } from "react-toastify"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: ExhibitionPricing | null
  exhibitionId: number
}

const ExhibitionPricingModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData, exhibitionId }) => {
  const [form, setForm] = useState<Omit<ExhibitionPricing, "id">>({
    exhibition: exhibitionId,
    badge_fee: 0,
    badge_reprint_price: 0,
    visitor_entry_fee: 0,
    parking_fee_per_minute: 0,
    currency: "RWF",
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

 

useEffect(() => {
  if (exhibitionId === 0) return; // 👈 Prevent setting form with invalid exhibitionId

  if (initialData) {
    setForm({
      exhibition: initialData.exhibition,
      badge_fee: initialData.badge_fee,
      badge_reprint_price: initialData.badge_reprint_price,
      visitor_entry_fee: initialData.visitor_entry_fee,
      parking_fee_per_minute: initialData.parking_fee_per_minute,
      currency: initialData.currency,
    })
  } else {
    setForm({
      exhibition: exhibitionId,
      badge_fee: 0,
      badge_reprint_price: 0,
      visitor_entry_fee: 0,
      parking_fee_per_minute: 0,
      currency: "RWF",
    })
  }
}, [initialData, exhibitionId])





  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setForm({
      ...form,
      [name]: type === "number" ? Number(value) : value,
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (form.badge_fee < 0) newErrors.badge_fee = "Badge fee cannot be negative"
    if (form.badge_reprint_price < 0) newErrors.badge_reprint_price = "Badge reprint price cannot be negative"
    if (form.visitor_entry_fee < 0) newErrors.visitor_entry_fee = "Visitor entry fee cannot be negative"
    if (form.parking_fee_per_minute < 0) newErrors.parking_fee_per_minute = "Parking fee cannot be negative"
    if (!form.currency) newErrors.currency = "Currency is required"

    return newErrors
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      if (initialData) {
        await ExhibitionPricingService.update(initialData.id, form)
        toast.success("Pricing updated successfully!")
      } else {
        await ExhibitionPricingService.create(form)
        toast.success("Pricing created successfully!")
      }
      onSuccess()
      toggle()
    } catch (error) {
      console.error("Error saving pricing:", error)
      toast.error("Something went wrong. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{initialData ? "Edit" : "Add"} Exhibition Pricing</ModalHeader>
      <ModalBody>
        <div className="row">
          <FormGroup className="col-md-6">
            <Label for="badge_fee">Badge Fee</Label>
            <Input
              type="number"
              name="badge_fee"
              value={form.badge_fee}
              onChange={handleChange}
              step="0.01"
              min="0"
              invalid={!!errors.badge_fee}
            />
            <FormFeedback>{errors.badge_fee}</FormFeedback>
          </FormGroup>

          <FormGroup className="col-md-6">
            <Label for="badge_reprint_price">Badge Reprint Price</Label>
            <Input
              type="number"
              name="badge_reprint_price"
              value={form.badge_reprint_price}
              onChange={handleChange}
              step="0.01"
              min="0"
              invalid={!!errors.badge_reprint_price}
            />
            <FormFeedback>{errors.badge_reprint_price}</FormFeedback>
          </FormGroup>
        </div>

        <div className="row">
          <FormGroup className="col-md-6">
            <Label for="visitor_entry_fee">Visitor Entry Fee</Label>
            <Input
              type="number"
              name="visitor_entry_fee"
              value={form.visitor_entry_fee}
              onChange={handleChange}
              step="0.01"
              min="0"
              invalid={!!errors.visitor_entry_fee}
            />
            <FormFeedback>{errors.visitor_entry_fee}</FormFeedback>
          </FormGroup>

          <FormGroup className="col-md-6">
            <Label for="parking_fee_per_minute">Parking Fee per Minute</Label>
            <Input
              type="number"
              name="parking_fee_per_minute"
              value={form.parking_fee_per_minute}
              onChange={handleChange}
              step="0.01"
              min="0"
              invalid={!!errors.parking_fee_per_minute}
            />
            <FormFeedback>{errors.parking_fee_per_minute}</FormFeedback>
          </FormGroup>
        </div>

        <FormGroup>
          <Label for="currency">Currency</Label>
          <Input
            type="select"
            name="currency"
            value={form.currency}
            onChange={handleChange}
            invalid={!!errors.currency}
          >
            <option value="">-- Select Currency --</option>
            <option value="RWF">RWF - Rwandan Franc</option>
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
           
          </Input>
          <FormFeedback>{errors.currency}</FormFeedback>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : initialData ? "Update" : "Create"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ExhibitionPricingModal
